import React from 'react';
import { Modal, Form, Select, Checkbox, Button, notification } from 'antd';
import { useClinics } from '../../../hooks/useClinics';
import { useCreateShift, useUpdateShift } from '../../../hooks/useShifts';
import { UserOutlined, CalendarOutlined, TeamOutlined } from '@ant-design/icons';

const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
];

// "08:30:00" -> { ticks } (1 tick = 100ns, so 10,000,000 ticks per second)
// Backend expects TimeSpan as "HH:mm:ss"
const toTimeSpan = (time) => {
    if (!time) return "00:00:00";
    return time.split(":").length === 2 ? `${time}:00` : time;
};

const ShiftFormModal = ({ visible, onCancel, specialists, onSubmit, editingShift }) => {
    const [form] = Form.useForm();
    const { clinicsQuery } = useClinics({ pageSize: 100 });
    const createShiftMutation = useCreateShift();
    const updateShiftMutation = useUpdateShift();

    const isEdit = !!editingShift;
    const isLoading = createShiftMutation.isLoading || updateShiftMutation.isLoading;

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!values.workingDays || values.workingDays.length === 0) {
                notification.error({
                    message: 'Validation Error',
                    description: 'Please select at least one working day'
                });
                return;
            }

            if (isEdit) {
                // Keep existing time slots so the update doesn't wipe them
             const existingHours = (editingShift.hours || []).map((h) => ({
    startTime: toTimeSpan(h.startTime),
    endTime: toTimeSpan(h.endTime),
    maxPatients: h.maxPatients ?? 0,
}));

                await updateShiftMutation.mutateAsync({
                    shiftId: editingShift.shiftId,
                    days: values.workingDays.map((dayValue) => ({
                        dayOfWeek: dayValue,
                        isWorking: true,
                        hours: existingHours,
                    })),
                });
            } else {
                await createShiftMutation.mutateAsync({
                    specialistId: values.specialistId,
                    clinicId: values.clinicId,
                    days: values.workingDays.map((dayValue) => ({
                        dayOfWeek: dayValue,
                        isWorking: true,
                        hours: []
                    }))
                });
            }

            if (onSubmit) onSubmit();
            onCancel();
        } catch (err) {
            console.error('Submission error:', err);
        }
    };

    const clinics = clinicsQuery.data?.data?.clinics || clinicsQuery.data?.clinics || [];

    const initialValues = isEdit
        ? {
              specialistId: editingShift.specialistId,
              clinicId: editingShift.clinicId,
              workingDays: (editingShift.days || [])
                  .filter((d) => d.isWorking !== false)
                  .map((d) => d.dayOfWeek),
          }
        : {};

    return (
        <Modal
            title={
                <div className="flex items-center space-x-2">
                    <CalendarOutlined className="text-primary text-xl" />
                    <span className="text-lg font-semibold">
                        {isEdit ? 'Edit Shift' : 'Create New Shift'}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>Cancel</Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={handleSubmit}
                    loading={isLoading}
                    style={{ backgroundColor: '#298d9f', borderColor: '#298d9f' }}
                >
                    {isEdit ? 'Save Changes' : 'Create Shift'}
                </Button>
            ]}
            destroyOnClose
            width={600}
        >
            <div className="py-2">
                <Form
                    key={editingShift?.shiftId || 'new'}
                    form={form}
                    layout="vertical"
                    initialValues={initialValues}
                >
                    <Form.Item
                        name="specialistId"
                        label={<span className="font-medium"><TeamOutlined className="mr-2" />Specialist</span>}
                        rules={[{ required: true, message: 'Please select a specialist' }]}
                    >
                        <Select
                            placeholder="Select a specialist"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                            disabled={isEdit}
                        >
                            {specialists.map(spec => (
                                <Select.Option key={spec.specialistId} value={spec.specialistId}>
                                    {spec.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="clinicId"
                        label={<span className="font-medium"><UserOutlined className="mr-2" />Clinic</span>}
                        rules={[{ required: true, message: 'Please select a clinic' }]}
                    >
                        <Select placeholder="Select clinic" size="large" showSearch disabled={isEdit}>
                            {clinics.map(clinic => (
                                <Select.Option key={clinic.clinicId} value={clinic.clinicId}>
                                    {clinic.clinicName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="workingDays"
                        label={<span className="font-medium"><CalendarOutlined className="mr-2" />Select Working Days</span>}
                        rules={[{ required: true, message: 'Select at least one day', type: 'array', min: 1 }]}
                    >
                        <Checkbox.Group style={{ width: '100%' }}>
                            <div className="grid grid-cols-2 gap-3">
                                {DAYS_OF_WEEK.map(day => (
                                    <Checkbox key={day.value} value={day.value} className="p-2 border rounded hover:border-primary transition-colors">
                                        <span className="font-medium">{day.label}</span>
                                    </Checkbox>
                                ))}
                            </div>
                        </Checkbox.Group>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default ShiftFormModal;